import { toast } from 'sonner';
import { format } from 'date-fns';
import { EditorContent } from '@tiptap/react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { forwardRef, type ReactNode, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  ArrowUpRight,
  CheckCircle2,
  Clock,
  FileText,
  History,
  Loader2,
  Mic,
  Pencil,
  Play,
  Plus,
  Save,
  Search,
  Send,
  Sparkles,
  Stethoscope,
  Upload,
  User,
  XCircle,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { useAudioUpload } from '@/lib/hooks';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useMembers } from '@/features/members';
import { audioDB } from '@/lib/storage/audioDB';
import { Separator } from '@/components/ui/separator';
import { AudioBar } from '@/components/encounter/AudioBar';
import { P, RequirePermission } from '@/features/permissions';
import type { EnhancedJsonSchema } from '@/features/emr/types';
import { RecordingsList } from '@/components/audio/RecordingsList';
import { useTemplates } from '@/features/templates/api/templates-api';
import { ReferencePanel } from '@/components/encounter/ReferencePanel';
import { DocumentViewerSheet } from '../components/DocumentViewerSheet';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useDocumentEditor } from '../components/editor/useDocumentEditor';
import type { RecordingsListRef } from '@/components/audio/RecordingsList';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { GenerateDocumentDialog } from '../components/GenerateDocumentDialog';
import { useEncounterSessionStore } from '@/lib/stores/encounter-session-store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { type GeneratedDocumentOut, useActionExecutions } from '../api/documents-api';
import { useContextViewTemplates, usePregnancies } from '@/features/emr/api/pregnancies-api';
import { type PatientOut, useCreatePatient, usePatient, usePatients } from '../api/patients-api';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  encounterTypeBadgeStyles,
  encounterTypeLabels,
  encounterTypeToggleColors,
  documentStatusBadgeStyles as statusBadgeStyles,
} from '../constants';
import {
  useEncounter,
  useEncounterAudioSegments,
  useEncounterDocuments,
  useEncounterRevisions,
  useUpdateEncounter,
  useUploadAudioSegment,
} from '../api/encounters-api';

const actionStatusConfig: Record<string, { icon: ReactNode; bg: string }> = {
  failed: {
    bg: 'bg-red-50 text-red-600',
    icon: <XCircle className="h-4 w-4" />,
  },
  pending: {
    bg: 'bg-amber-50 text-amber-600',
    icon: <Clock className="h-4 w-4" />,
  },
  cancelled: {
    bg: 'bg-slate-50 text-slate-500',
    icon: <XCircle className="h-4 w-4" />,
  },
  completed: {
    bg: 'bg-emerald-50 text-emerald-600',
    icon: <CheckCircle2 className="h-4 w-4" />,
  },
  running: {
    bg: 'bg-blue-50 text-blue-600',
    icon: <Loader2 className="h-4 w-4 animate-spin" />,
  },
};

type ClinicalContext = {
  chief_complaint?: string;
  assessment?: string;
  plan?: string;
  vitals?: Record<string, string>;
  medications?: string[];
  lab_values?: Record<string, string>;
  template_id?: string;
  template_context?: Record<string, string>;
  context_view_template_id?: string;
};

// ─── Main Component ─────────────────────────────────────────────

interface DraftEditorHandle {
  save: () => void;
  submit: () => void;
}

// ─── Header Stat ────────────────────────────────────────────────

interface DraftEditorProps {
  audioSegments: import('../api/encounters-api').AudioSegmentOut[];
  ctx: ClinicalContext | null;
  documents: GeneratedDocumentOut[];
  encounter: import('../api/encounters-api').EncounterOut;
  encounterId: string;
  onGenerateDocument: () => void;
  onSavingChange: (saving: boolean) => void;
  onViewDocument: (doc: GeneratedDocumentOut) => void;
  templates: import('@/features/templates/api/templates-api').TemplateOut[];
  templatesMap: Map<string, import('@/features/templates/api/templates-api').TemplateOut>;
}

// ─── Draft Editor ───────────────────────────────────────────────

export function EncounterDetailPage() {
  const { encounterId } = useParams({ strict: false }) as { encounterId: string };
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();
  const [activeTab, setActiveTab] = useState('documents');
  const [viewingDoc, setViewingDoc] = useState<GeneratedDocumentOut | null>(null);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const draftRef = useRef<DraftEditorHandle>(null);

  const { data: encounter, isLoading: loadingEncounter } = useEncounter(encounterId);
  const { data: patient, isLoading: loadingPatient } = usePatient(encounter?.patient_id ?? undefined);
  const { data: documents = [] } = useEncounterDocuments(encounterId);
  const { data: revisions = [] } = useEncounterRevisions(encounterId);
  const { data: audioSegments = [] } = useEncounterAudioSegments(encounterId);
  const { data: actionsData } = useActionExecutions(encounterId ? { encounter_id: encounterId } : undefined);
  const { data: templatesData } = useTemplates({ limit: 100, category: 'document' });

  const isDraft = encounter?.status === 'draft';
  const actions = actionsData?.data ?? [];
  const templates = templatesData?.data ?? [];
  const templatesMap = new Map(templates.map((t) => [t.id, t]));

  const meta = patient?.metadata_;
  const patientDisplayName = meta
    ? `${meta.last_name || ''}, ${meta.first_name || ''}`.replace(/^, |, $/g, '')
    : (patient?.medical_record_number ?? '');

  const [draftSaving, setDraftSaving] = useState(false);

  useEffect(() => {
    if (!encounter) return;
    setPageHeader({
      title: `Encounter - ${format(new Date(encounter.encounter_date), 'MMM d, yyyy')}`,
      actions: isDraft ? (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={draftSaving} onClick={() => draftRef.current?.save()}>
            {draftSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save Draft
          </Button>
          <Button size="sm" disabled={draftSaving} onClick={() => draftRef.current?.submit()}>
            {draftSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Submit
          </Button>
        </div>
      ) : encounter.status === 'submitted' ? (
        <Badge variant="outline" className="gap-1.5 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 border-blue-200">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Processing...
        </Badge>
      ) : (
        <RequirePermission permission={P.GENERATE_DOCUMENT}>
          <Button size="sm" variant="outline" onClick={() => setShowGenerateDialog(true)}>
            <Sparkles className="mr-2 h-4 w-4" />
            Generate Document
          </Button>
        </RequirePermission>
      ),
    });
    return () => setPageHeader(null);
  }, [encounter, setPageHeader, isDraft, draftSaving]);

  if (loadingEncounter || loadingPatient) {
    return (
      <div className="flex items-center justify-center p-16">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">Loading encounter...</p>
        </div>
      </div>
    );
  }

  if (!encounter) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2">
        <FileText className="h-10 w-10 text-muted-foreground/40" />
        <p className="text-muted-foreground">Encounter not found.</p>
        <Button size="sm" variant="ghost" onClick={() => navigate({ to: '/encounters' })}>
          Back to encounters
        </Button>
      </div>
    );
  }

  const ctx = encounter.clinical_context as ClinicalContext | null;
  const encounterType = encounter.encounter_type ?? 'initial';

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {/* Patient Section */}
      <PatientSection isDraft={isDraft} patient={patient} navigate={navigate} patientDisplayName={patientDisplayName} />

      {/* Encounter Header */}
      <div className="rounded-xl border bg-card">
        <div className="p-5 md:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="space-y-2">
              <h1 className="text-xl font-semibold tracking-tight md:text-2xl">
                {format(new Date(encounter.encounter_date), 'EEEE, MMMM d, yyyy')}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                {encounter.encounter_type && (
                  <Badge
                    variant="outline"
                    className={cn('text-xs font-medium', encounterTypeBadgeStyles[encounterType])}>
                    {encounterTypeLabels[encounterType] ?? encounterType}
                  </Badge>
                )}
                <Badge
                  variant="outline"
                  className={cn('text-xs font-medium capitalize', statusBadgeStyles[encounter.status])}>
                  {encounter.status}
                </Badge>
                {encounter.clinician && (
                  <>
                    <Separator className="h-4" orientation="vertical" />
                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Stethoscope className="h-3.5 w-3.5" />
                      {encounter.clinician.name}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-5">
              <HeaderStat label="Documents" value={documents.length} icon={<FileText className="h-4 w-4" />} />
              <HeaderStat label="Recordings" value={audioSegments.length} icon={<Mic className="h-4 w-4" />} />
              {revisions.length > 0 && (
                <HeaderStat
                  label="Revision"
                  value={revisions[0].revision_number}
                  icon={<History className="h-4 w-4" />}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {isDraft ? (
        <DraftEditor
          ref={draftRef}
          ctx={ctx}
          documents={documents}
          encounter={encounter}
          templates={templates}
          encounterId={encounterId}
          templatesMap={templatesMap}
          audioSegments={audioSegments}
          onViewDocument={setViewingDoc}
          onSavingChange={setDraftSaving}
          onGenerateDocument={() => setShowGenerateDialog(true)}
        />
      ) : (
        <ReadOnlyView
          actions={actions}
          activeTab={activeTab}
          documents={documents}
          encounter={encounter}
          revisions={revisions}
          encounterId={encounterId}
          setActiveTab={setActiveTab}
          templatesMap={templatesMap}
          audioSegments={audioSegments}
          onViewDocument={setViewingDoc}
          onGenerateDocument={() => setShowGenerateDialog(true)}
        />
      )}

      <DocumentViewerSheet
        open={!!viewingDoc}
        document={viewingDoc}
        onOpenChange={(open) => {
          if (!open) setViewingDoc(null);
        }}
      />

      <GenerateDocumentDialog
        encounterId={encounterId}
        open={showGenerateDialog}
        patientId={encounter?.patient_id ?? ''}
        onOpenChange={setShowGenerateDialog}
      />
    </div>
  );
}

function HeaderStat({ icon, value, label }: { icon: ReactNode; value: number; label: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground">
        {icon}
      </div>
      <div>
        <p className="text-lg font-semibold leading-none tabular-nums">{value}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      </div>
    </div>
  );
}

const DraftEditor = forwardRef<DraftEditorHandle, DraftEditorProps>(function DraftEditor(
  {
    ctx,
    encounter,
    documents,
    templates,
    encounterId,
    templatesMap,
    audioSegments,
    onSavingChange,
    onViewDocument,
    onGenerateDocument,
  },
  ref,
) {
  const recordingsListRef = useRef<RecordingsListRef>(null);
  const updateEncounter = useUpdateEncounter(encounterId);
  const uploadAudio = useUploadAudioSegment(encounterId);
  const { data: membersData } = useMembers();
  const activeMembers = (membersData ?? []).filter((m) => m.is_active);

  // Active pregnancy for the encounter's patient. Backend does not auto-link
  // pregnancy_id on draft creation, so fall back to the patient's active
  // pregnancy — otherwise OB flow-sheet misses PregnancySummary.
  const { data: pregnanciesData } = usePregnancies(encounter.patient_id ?? undefined);
  const activePregnancyId = pregnanciesData?.data?.find((p) => p.status === 'active')?.id ?? null;
  const effectivePregnancyId = encounter.pregnancy_id ?? activePregnancyId;

  const [isDragging, setIsDragging] = useState(false);

  const [encounterType, setEncounterType] = useState(encounter.encounter_type ?? '');
  const [encounterDate, setEncounterDate] = useState(encounter.encounter_date);
  const [assignedTo, setAssignedTo] = useState(encounter.assigned_to ?? '');
  const [selectedTemplateId, setSelectedTemplateId] = useState(ctx?.template_id ?? '');

  const { data: contextViews } = useContextViewTemplates();
  const contextViewTemplates = (contextViews ?? []) as Array<{
    id: string;
    name: string;
    json_schema: EnhancedJsonSchema;
  }>;
  // Restore the saved context view from the encounter; fall back to null so the
  // effect below can default it to General Visit on a truly fresh draft.
  const [contextViewId, setContextViewId] = useState<string | null>(ctx?.context_view_template_id ?? null);

  // Auto-select "General Visit" as default context view only if the encounter
  // didn't persist one and the user hasn't picked one yet.
  useEffect(() => {
    if (contextViewId) return;
    if (ctx?.context_view_template_id) return;
    if (contextViewTemplates.length === 0) return;
    const generalView = contextViewTemplates.find((t) => t.name === 'General Visit');
    if (generalView) setContextViewId(generalView.id);
  }, [contextViewId, contextViewTemplates, ctx]);

  // Recover recordings from IndexedDB (e.g., after pill-stop navigates back)
  const { recordingMeta } = useEncounterSessionStore();
  useEffect(() => {
    async function recoverRecordings() {
      const stored = await audioDB.getAll();
      if (stored.length === 0) return;
      const existing = recordingsListRef.current?.getRecordings() || [];
      const existingIds = new Set(existing.map((r) => r.id));
      for (const rec of stored) {
        if (!existingIds.has(rec.id)) {
          recordingsListRef.current?.addRecording({
            id: rec.id,
            blob: rec.blob,
            duration: rec.duration,
            timestamp: new Date(rec.createdAt),
            name: `Recording ${existing.length + 1}`,
          });
        }
      }
    }
    if (recordingMeta.status === 'stopped' || recordingMeta.status === 'idle') {
      recoverRecordings();
    }
  }, [recordingMeta.status]);

  // Load existing audio segments into recordings list
  // Sync server audio segments into recordings list, keeping unsaved local recordings
  useEffect(() => {
    if (!recordingsListRef.current) return;
    const current = recordingsListRef.current.getRecordings();
    // Keep only unsaved local recordings (have blob, no segmentId)
    const unsaved = current.filter((r) => r.blob && !r.segmentId);
    // Build list from server segments
    const serverRecordings = audioSegments.map((seg) => ({
      id: seg.id,
      url: seg.audio_url,
      segmentId: Number(seg.id),
      duration: seg.duration || 0,
      timestamp: new Date(seg.created_at),
      name: seg.audio_url.split('/').pop() || `Segment ${seg.order}`,
    }));
    recordingsListRef.current.setRecordings([...serverRecordings, ...unsaved]);
  }, [audioSegments]);

  const handleTemplateChange = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId);
  }, []);

  // ─── Audio handlers ─────────────────────────────────────────

  const handleAudioUploadError = useCallback((error: string) => {
    toast.error(error);
  }, []);

  const handleAudioUploadSuccess = useCallback(
    (result: { blob: Blob; fileName: string; metadata: { duration: number } }) => {
      const newRecording = {
        blob: result.blob,
        timestamp: new Date(),
        name: result.fileName,
        id: crypto.randomUUID(),
        duration: result.metadata.duration,
      };
      recordingsListRef.current?.addRecording(newRecording);
    },
    [],
  );

  const audioUpload = useAudioUpload({
    maxFileSize: 100 * 1024 * 1024,
    onError: handleAudioUploadError,
    onSuccess: handleAudioUploadSuccess,
  });

  function buildClinicalContext(): Record<string, unknown> {
    return {
      template_id: selectedTemplateId || undefined,
      context_view_template_id: contextViewId || undefined,
    };
  }

  const handleSave = useCallback(async () => {
    onSavingChange(true);
    try {
      await updateEncounter.mutateAsync({
        encounter_date: encounterDate,
        assigned_to: assignedTo || undefined,
        clinical_context: buildClinicalContext(),
        encounter_type: encounterType || undefined,
      });

      // Upload new recordings (those with blob but no segmentId)
      const recordings = recordingsListRef.current?.getRecordings() ?? [];
      const newRecordings = recordings.filter((r) => r.blob && !r.segmentId);
      for (let i = 0; i < newRecordings.length; i++) {
        const rec = newRecordings[i];
        if (!rec.blob) continue;
        try {
          const segment = await uploadAudio.mutateAsync({
            file: rec.blob,
            fileName: rec.name,
            order: audioSegments.length + i,
          });
          // Mark as saved so it won't be re-uploaded
          recordingsListRef.current?.removeRecording(rec.id);
          recordingsListRef.current?.addRecording({
            ...rec,
            blob: undefined,
            url: segment.audio_url,
            segmentId: Number(segment.id),
          });
        } catch {
          toast.error(`Failed to upload ${rec.name}`);
        }
      }

      toast.success('Draft saved');
    } catch {
      toast.error('Failed to save draft');
    } finally {
      onSavingChange(false);
    }
  }, [
    updateEncounter,
    uploadAudio,
    encounterType,
    encounterDate,
    assignedTo,
    onSavingChange,
    selectedTemplateId,
    contextViewId,
    audioSegments.length,
  ]);

  const handleSubmit = useCallback(async () => {
    onSavingChange(true);
    try {
      await updateEncounter.mutateAsync({
        status: 'submitted',
        encounter_date: encounterDate,
        assigned_to: assignedTo || undefined,
        clinical_context: buildClinicalContext(),
        encounter_type: encounterType || undefined,
      });
      // Recordings are now on the server; drop the local IndexedDB cache.
      await audioDB.deleteAll();
      toast.success('Encounter submitted');
    } catch {
      toast.error('Failed to submit encounter');
    } finally {
      onSavingChange(false);
    }
  }, [updateEncounter, encounterType, encounterDate, assignedTo, onSavingChange, selectedTemplateId, contextViewId]);

  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      submit: handleSubmit,
    }),
    [handleSave, handleSubmit],
  );

  return (
    <div className="flex flex-col gap-6">
      {/* AudioBar (replaces inline audio section) */}
      <AudioBar
        onRecordingStopped={(blob, duration) => {
          if (duration === 0) return;
          const current = recordingsListRef.current?.getRecordings() || [];
          recordingsListRef.current?.addRecording({
            blob,
            duration,
            timestamp: new Date(),
            id: crypto.randomUUID(),
            name: `Recording ${current.length + 1}`,
          });
        }}
      />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left: Clinical Context */}
        <div className="min-w-0 space-y-6">
          {/* Context View selector */}
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Context View:</Label>
            <Select value={contextViewId ?? ''} onValueChange={(v) => setContextViewId(v || null)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select view..." />
              </SelectTrigger>
              <SelectContent>
                {contextViewTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Encounter Details */}
          <Card className="border-border/60">
            <CardHeader className="pb-4">
              <CardTitle className="text-base flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Pencil className="h-3.5 w-3.5" />
                </div>
                Encounter Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label className="text-sm">Encounter Type</Label>
                <ToggleGroup
                  type="single"
                  variant="outline"
                  className="w-full"
                  value={encounterType}
                  onValueChange={(value) => {
                    if (value) setEncounterType(value);
                  }}>
                  {(['initial', 'follow-up', 'urgent', 'telehealth'] as const).map((type) => (
                    <ToggleGroupItem
                      key={type}
                      value={type}
                      className={cn('capitalize text-sm', encounterTypeToggleColors[type])}>
                      {type}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm">Encounter Date</Label>
                  <Input type="date" value={encounterDate} onChange={(e) => setEncounterDate(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Assigned Clinician</Label>
                  <Select value={assignedTo} onValueChange={setAssignedTo}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select clinician..." />
                    </SelectTrigger>
                    <SelectContent>
                      {activeMembers.map((member) => (
                        <SelectItem key={member.user_id} value={member.user_id}>
                          {member.user_name ?? member.user_email ?? 'Unknown'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Template Selection */}
          <Card className="border-border/60">
            <CardHeader className="pb-4">
              <CardTitle className="text-base flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Sparkles className="h-3.5 w-3.5" />
                </div>
                Template & Generation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm">Template</Label>
                <Select
                  value={selectedTemplateId || '__none__'}
                  onValueChange={(v) => handleTemplateChange(v === '__none__' ? '' : v)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select template..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" className="text-muted-foreground">
                      No template
                    </SelectItem>
                    {templates
                      .filter((t) => t.is_active)
                      .map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {encounter.status !== 'draft' && (
                <RequirePermission permission={P.GENERATE_DOCUMENT}>
                  <Button variant="outline" className="w-full" onClick={onGenerateDocument}>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate Document
                  </Button>
                </RequirePermission>
              )}
            </CardContent>
          </Card>

          {/* Audio Files */}
          <Card className="border-border/60">
            <CardHeader className="pb-4">
              <CardTitle className="text-base flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Mic className="h-3.5 w-3.5" />
                </div>
                Audio Files
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <input
                id="draft-audio-file-input"
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) await audioUpload.upload(file);
                }}
              />
              <label
                htmlFor="draft-audio-file-input"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragging(false);
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragging(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file && file.type.startsWith('audio/')) {
                    await audioUpload.upload(file);
                  } else if (file) {
                    toast.error('Please upload an audio file');
                  }
                }}>
                <div
                  className={cn(
                    'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-6 transition-all cursor-pointer',
                    isDragging && 'border-primary bg-primary/5 scale-[1.01]',
                    !isDragging && 'border-muted-foreground/20 hover:border-primary/40 hover:bg-muted/30',
                  )}>
                  <Upload
                    className={cn('h-5 w-5 transition-colors', isDragging ? 'text-primary' : 'text-muted-foreground')}
                  />
                  <div className="text-center space-y-0.5">
                    <p className="text-sm font-medium">{isDragging ? 'Drop audio file here' : 'Upload audio file'}</p>
                    <p className="text-xs text-muted-foreground">Drag & drop or click to browse</p>
                  </div>
                </div>
              </label>

              <RecordingsList ref={recordingsListRef} />
            </CardContent>
          </Card>

          {documents.length > 0 && (
            <Card className="border-border/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  <span>Generated Documents</span>
                  <Badge className="text-xs" variant="secondary">
                    {documents.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {documents.map((doc) => {
                  const tmpl = doc.template_id ? templatesMap.get(doc.template_id) : undefined;
                  return (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 rounded-lg border border-border/60 p-3 cursor-pointer hover:bg-muted/40 transition-colors"
                      onClick={() => onViewDocument(doc)}>
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <FileText className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{tmpl?.name || 'Document'}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(doc.generated_at), 'MMM d, h:mm a')}
                        </p>
                      </div>
                      <Badge variant="outline" className={cn('text-[10px] shrink-0', statusBadgeStyles[doc.status])}>
                        {doc.status}
                      </Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Reference Panel */}
        <div className="min-w-0 min-h-[400px] lg:sticky lg:top-4 lg:self-start">
          <ReferencePanel
            currentEncounterId={encounterId}
            pregnancyId={effectivePregnancyId}
            contextViewTemplateId={contextViewId}
            patientId={encounter.patient_id ?? ''}
          />
        </div>
      </div>
    </div>
  );
});

// ─── Read-Only View ─────────────────────────────────────────────

function CreatePatientDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const createPatient = useCreatePatient();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mrn, setMrn] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');

  function reset() {
    setFirstName('');
    setLastName('');
    setMrn('');
    setDob('');
    setGender('');
  }

  async function handleCreate() {
    if (!firstName.trim() || !lastName.trim() || !mrn.trim()) {
      toast.error('First name, last name, and MRN are required');
      return;
    }

    try {
      await createPatient.mutateAsync({
        medical_record_number: mrn.trim(),
        metadata_: {
          last_name: lastName.trim(),
          gender: gender || undefined,
          first_name: firstName.trim(),
          date_of_birth: dob || undefined,
        },
      });
      toast.success(`Patient ${lastName}, ${firstName} created`);
      reset();
      onOpenChange(false);
    } catch {
      toast.error('Failed to create patient');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Patient</DialogTitle>
          <DialogDescription>Add a new patient record. You can add more details later.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">
                First Name <span className="text-destructive">*</span>
              </Label>
              <Input value={firstName} placeholder="John" onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">
                Last Name <span className="text-destructive">*</span>
              </Label>
              <Input value={lastName} placeholder="Smith" onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">
              Medical Record Number <span className="text-destructive">*</span>
            </Label>
            <Input value={mrn} placeholder="MRN-00001" onChange={(e) => setMrn(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">Date of Birth</Label>
            <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Gender</Label>
            <RadioGroup value={gender} className="flex gap-4" onValueChange={setGender}>
              {(
                [
                  ['M', 'Male'],
                  ['F', 'Female'],
                  ['O', 'Other'],
                ] as const
              ).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value={value} />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={createPatient.isPending} onClick={handleCreate}>
            {createPatient.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Patient
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Transcript Viewer (read-only Tiptap) ───────────────────────

function EmptyState({
  icon,
  action,
  message,
  description,
}: {
  icon: ReactNode;
  message: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <div className="text-muted-foreground/30 mb-3">{icon}</div>
      <p className="font-medium text-muted-foreground">{message}</p>
      {description && <p className="text-sm text-muted-foreground/70 mt-1 max-w-xs">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────

function PatientSection({
  patient,
  isDraft,
  navigate,
  patientDisplayName,
}: {
  patient: PatientOut | undefined;
  patientDisplayName: string;
  isDraft: boolean;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data: patientsData } = usePatients(popoverOpen ? { limit: 8, search: search || undefined } : undefined);
  const patients = patientsData?.data ?? [];

  const meta = patient?.metadata_;
  const dob = meta?.date_of_birth ? format(new Date(meta.date_of_birth), 'MMM d, yyyy') : null;
  const gender = meta?.gender === 'M' ? 'Male' : meta?.gender === 'F' ? 'Female' : (meta?.gender ?? null);

  return (
    <>
      <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
          <User className="h-4.5 w-4.5 text-muted-foreground" />
        </div>

        <div className="flex-1 min-w-0">
          {patient ? (
            <>
              <p className="text-sm font-semibold">{patientDisplayName}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                <span>MRN: {patient.medical_record_number}</span>
                {dob && (
                  <>
                    <span className="text-border">&middot;</span>
                    <span>DOB: {dob}</span>
                  </>
                )}
                {gender && (
                  <>
                    <span className="text-border">&middot;</span>
                    <span>{gender}</span>
                  </>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No patient selected</p>
          )}
        </div>

        {isDraft ? (
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" className="shrink-0">
                <Search className="mr-1.5 h-3.5 w-3.5" />
                {patient ? 'Change' : 'Select Patient'}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <div className="p-3 border-b border-border/60">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    value={search}
                    className="h-8 pl-8 text-sm"
                    placeholder="Search by name or MRN..."
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="max-h-56 overflow-y-auto p-1">
                {patients.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    {search ? 'No patients found' : 'Start typing to search...'}
                  </p>
                ) : (
                  patients.map((p) => {
                    const m = p.metadata_;
                    const name = m
                      ? `${m.last_name || ''}, ${m.first_name || ''}`.replace(/^, |, $/g, '')
                      : p.medical_record_number;
                    const isSelected = patient?.id === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => {
                          if (!isSelected) {
                            toast.info('Patient selection saved on next draft save');
                          }
                          setPopoverOpen(false);
                          setSearch('');
                        }}
                        className={cn(
                          'w-full flex items-center gap-2.5 rounded-lg p-2 text-left text-sm transition-colors',
                          isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
                        )}>
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                          <User className="h-3 w-3" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate text-sm">{name}</p>
                          <p className="text-[11px] text-muted-foreground">MRN: {p.medical_record_number}</p>
                        </div>
                        {isSelected && <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />}
                      </button>
                    );
                  })
                )}
              </div>
              <div className="border-t border-border/60 p-1.5">
                <button
                  className="w-full flex items-center gap-2 rounded-lg p-2 text-sm text-primary hover:bg-primary/5 transition-colors"
                  onClick={() => {
                    setPopoverOpen(false);
                    setSearch('');
                    setCreateOpen(true);
                  }}>
                  <Plus className="h-4 w-4" />
                  Create New Patient
                </button>
              </div>
            </PopoverContent>
          </Popover>
        ) : patient ? (
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={() => navigate({ to: '/patients/$patientId', params: { patientId: patient.id } })}>
            View Profile
            <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>

      <CreatePatientDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}

// ─── Patient Section ────────────────────────────────────────────

function ReadOnlyView({
  actions,
  encounter,
  activeTab,
  documents,
  revisions,
  encounterId,
  setActiveTab,
  templatesMap,
  audioSegments,
  onViewDocument,
  onGenerateDocument,
}: {
  encounter: import('../api/encounters-api').EncounterOut;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  documents: GeneratedDocumentOut[];
  revisions: import('../api/encounters-api').EncounterRevisionOut[];
  audioSegments: import('../api/encounters-api').AudioSegmentOut[];
  actions: import('../api/documents-api').ActionExecutionOut[];
  templatesMap: Map<string, import('@/features/templates/api/templates-api').TemplateOut>;
  onGenerateDocument: () => void;
  onViewDocument: (doc: GeneratedDocumentOut) => void;
  encounterId: string;
}) {
  // Active pregnancy fallback when the encounter lacks a stored pregnancy_id.
  const { data: pregnanciesData } = usePregnancies(encounter.patient_id ?? undefined);
  const activePregnancyId = pregnanciesData?.data?.find((p) => p.status === 'active')?.id ?? null;
  const effectivePregnancyId = encounter.pregnancy_id ?? activePregnancyId;

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      {/* Left: Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start bg-muted/40">
          <TabsTrigger value="documents" className="gap-1.5">
            <Sparkles className="h-3.5 w-3.5" />
            Documents
            {documents.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-[10px] rounded-full">
                {documents.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="transcript" className="gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            Transcript
          </TabsTrigger>
          <TabsTrigger value="audio" className="gap-1.5">
            <Mic className="h-3.5 w-3.5" />
            Audio
          </TabsTrigger>
          <TabsTrigger value="revisions" className="gap-1.5">
            <History className="h-3.5 w-3.5" />
            Revisions
            {revisions.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-[10px] rounded-full">
                {revisions.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Transcript Tab */}
        <TabsContent className="mt-4" value="transcript">
          <Card className="border-border/60">
            <CardContent className="p-0">
              {encounter.transcript ? (
                <TranscriptViewer content={encounter.transcript} />
              ) : (
                <div className="p-5 md:p-6">
                  <EmptyState
                    message={
                      encounter.status === 'submitted' ? 'Transcript is being processed...' : 'No transcript available'
                    }
                    icon={
                      encounter.status === 'submitted' ? (
                        <Loader2 className="h-10 w-10 animate-spin" />
                      ) : (
                        <FileText className="h-10 w-10" />
                      )
                    }
                    description={
                      encounter.status === 'submitted'
                        ? 'Audio is being transcribed. This may take a few minutes.'
                        : "This encounter doesn't have a transcript yet."
                    }
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent className="mt-4" value="documents">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {documents.length} document{documents.length !== 1 ? 's' : ''} generated
              </p>
              {encounter.status !== 'submitted' && (
                <RequirePermission permission={P.GENERATE_DOCUMENT}>
                  <Button size="sm" variant="outline" onClick={onGenerateDocument}>
                    <Sparkles className="mr-2 h-3.5 w-3.5" />
                    Generate New
                  </Button>
                </RequirePermission>
              )}
            </div>

            {documents.length === 0 ? (
              <Card className="border-border/60">
                <CardContent className="p-0">
                  {encounter.status === 'submitted' ? (
                    <EmptyState
                      message="Document is being generated..."
                      icon={<Loader2 className="h-10 w-10 animate-spin" />}
                      description="The transcript is being processed and a document will be created automatically."
                    />
                  ) : (
                    <EmptyState
                      message="No documents yet"
                      icon={<FileText className="h-10 w-10" />}
                      description="Generate an AI-powered document from this encounter."
                      action={
                        <RequirePermission permission={P.GENERATE_DOCUMENT}>
                          <Button size="sm" variant="outline" onClick={onGenerateDocument}>
                            <Sparkles className="mr-2 h-3.5 w-3.5" />
                            Generate Document
                          </Button>
                        </RequirePermission>
                      }
                    />
                  )}
                </CardContent>
              </Card>
            ) : (
              documents.map((doc) => {
                const tmpl = doc.template_id ? templatesMap.get(doc.template_id) : undefined;
                return (
                  <div
                    key={doc.id}
                    className="group rounded-xl border border-border/60 bg-card p-4 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all"
                    onClick={() => onViewDocument(doc)}>
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{tmpl?.name || 'Document'}</p>
                          <Badge variant="outline" className={cn('text-[10px]', statusBadgeStyles[doc.status])}>
                            {doc.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {format(new Date(doc.generated_at), "MMM d, yyyy 'at' h:mm a")}
                        </p>
                        <p className="text-sm text-muted-foreground/80 mt-2 line-clamp-2 leading-relaxed">
                          {doc.content.slice(0, 140).replace(/[#*]/g, '')}...
                        </p>
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary transition-colors shrink-0 mt-0.5" />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Audio Tab */}
        <TabsContent value="audio" className="mt-4">
          <div className="space-y-3">
            {audioSegments.length === 0 ? (
              <Card className="border-border/60">
                <CardContent className="p-0">
                  <EmptyState
                    message="No audio recordings"
                    icon={<Mic className="h-10 w-10" />}
                    description="This encounter doesn't have any audio segments."
                  />
                </CardContent>
              </Card>
            ) : (
              audioSegments.map((seg) => (
                <div key={seg.id} className="rounded-xl border border-border/60 bg-card p-4">
                  <div className="flex items-center gap-4">
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-10 w-10 shrink-0 rounded-full hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                      <Play className="h-4 w-4" />
                    </Button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-sm">Segment {seg.order}</p>
                        <span className="text-xs text-muted-foreground tabular-nums font-medium">
                          {seg.duration
                            ? `${Math.floor(seg.duration / 60)}:${(seg.duration % 60).toString().padStart(2, '0')}`
                            : '--:--'}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full w-0 rounded-full bg-primary transition-all" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">
                        {format(new Date(seg.created_at), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                  {seg.transcription && (
                    <Accordion collapsible type="single" className="mt-3">
                      <AccordionItem value="transcription" className="border rounded-lg border-border/40 bg-muted/40">
                        <AccordionTrigger className="px-3 py-2 text-xs font-medium text-muted-foreground hover:no-underline">
                          <div className="flex items-center gap-1.5">
                            <FileText className="h-3.5 w-3.5" />
                            Transcription ({seg.transcription.split(/\s+/).length} words)
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-3 pb-3">
                          <TranscriptViewer content={seg.transcription} />
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Revisions Tab */}
        <TabsContent className="mt-4" value="revisions">
          {revisions.length === 0 ? (
            <Card className="border-border/60">
              <CardContent className="p-0">
                <EmptyState
                  message="No revision history"
                  icon={<History className="h-10 w-10" />}
                  description="Changes to this encounter will be tracked here."
                />
              </CardContent>
            </Card>
          ) : (
            <div className="relative space-y-0">
              {revisions.map((rev, i) => (
                <div key={rev.id} className="relative pl-8 pb-6 last:pb-0">
                  {/* Timeline connector */}
                  {i < revisions.length - 1 && <div className="absolute left-[13px] top-8 bottom-0 w-px bg-border" />}
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-border bg-background">
                    <span className="text-[10px] font-bold tabular-nums">{rev.revision_number}</span>
                  </div>
                  {/* Content */}
                  <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b border-border/40">
                      <p className="text-sm font-medium">Revision {rev.revision_number}</p>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(rev.created_at), 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>
                    <div className="p-4">
                      <p className="text-sm leading-relaxed text-foreground/80 max-h-32 overflow-y-auto">
                        {rev.transcript.slice(0, 400)}
                        {rev.transcript.length > 400 && <span className="text-muted-foreground">...</span>}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Actions Tab (hidden from tabs but kept for direct access) */}
        <TabsContent value="actions" className="mt-4">
          <div className="space-y-3">
            {actions.length === 0 ? (
              <Card className="border-border/60">
                <CardContent className="p-0">
                  <EmptyState
                    message="No workflow actions"
                    icon={<Send className="h-10 w-10" />}
                    description="Actions like document generation and EMR exports will appear here."
                  />
                </CardContent>
              </Card>
            ) : (
              actions.map((action) => {
                const tmpl = action.template_id ? templatesMap.get(action.template_id) : undefined;
                const config = actionStatusConfig[action.status] ?? {
                  bg: 'bg-slate-50 text-slate-500',
                  icon: <Clock className="h-4 w-4" />,
                };
                return (
                  <div key={action.id} className="rounded-xl border border-border/60 bg-card p-4">
                    <div className="flex items-start gap-3">
                      <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', config.bg)}>
                        {config.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm">{action.action.replace(/_/g, ' ')}</p>
                          <Badge
                            variant="outline"
                            className={cn('text-[10px] capitalize', statusBadgeStyles[action.status])}>
                            {action.status}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-muted-foreground">
                          {tmpl && (
                            <span className="flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              {tmpl.name}
                            </span>
                          )}
                          {action.started_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {format(new Date(action.started_at), 'MMM d, h:mm a')}
                            </span>
                          )}
                          {action.completed_at && action.started_at && (
                            <span className="tabular-nums">
                              {Math.round(
                                (new Date(action.completed_at).getTime() - new Date(action.started_at).getTime()) /
                                  1000,
                              )}
                              s duration
                            </span>
                          )}
                        </div>
                        {action.result && (
                          <div className="mt-3 rounded-lg bg-muted/40 p-3 border border-border/40">
                            <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                              {JSON.stringify(action.result, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Right: Reference Panel */}
      <div className="min-w-0 min-h-[400px] lg:sticky lg:top-4 lg:self-start">
        <ReferencePanel
          currentEncounterId={encounterId}
          pregnancyId={effectivePregnancyId}
          patientId={encounter.patient_id ?? ''}
          contextViewTemplateId={
            (
              encounter.clinical_context as {
                context_view_template_id?: string;
              } | null
            )?.context_view_template_id ?? null
          }
        />
      </div>
    </div>
  );
}

// ─── Create Patient Dialog ──────────────────────────────────────

function TranscriptViewer({ content }: { content: string }) {
  const editor = useDocumentEditor({ editable: false, initialContent: content });
  return <EditorContent editor={editor} />;
}
